import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DatePickerFr } from '../ui/DatePickerFr';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { Loader2, X, Upload, FileText, Trash2, Eye, Download, Printer, Search, Clock } from 'lucide-react';
import { DOC_TYPES } from './docTypesShared';
import { CustomDocumentTypesAdminPanel } from './CustomDocumentTypesAdminPanel';
import { AuditLogPanel } from './AuditLogPanel';
import useUploadDocumentModal from './useUploadDocumentModal';

export const UploadDocumentMembreModal = ({ membre, open, onClose, token, onDocumentUploaded, userRole }) => {
  const ctx = useUploadDocumentModal({ membre, open, token, onDocumentUploaded });
  const {
    ALL_DOC_TYPES, PICKER_TYPES, _applyAutoDate, _detectDateInFilename, _normalize, categoryFilter,
    computeVisibleDocuments, customTypes, dateOrigine, dateOrigineAuto, deletingId, description,
    documents, dragOver, exportingZip, fetchBlob, fetchDocuments, file,
    fileInputRef, formatSize, getTypeConfig, handleDelete, handleDownloadDoc, handleDrop,
    handleExportZip, handleFileChange, handlePrintDoc, handleUpload, handleView, loadingDocs,
    searchQuery, setCategoryFilter, setDateOrigine, setDateOrigineAuto, setDeletingId, setDescription,
    setDocuments, setDragOver, setExportingZip, setFile, setLoadingDocs, setSearchQuery,
    setTypeDocument, setUploading, typeDocument, uploading,
  } = ctx;

  if (!membre) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="upload-document-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-teal-600" />
            Inserer des documents pour {membre.prenom} {membre.nom}
          </DialogTitle>
          <DialogDescription>
            Documents contractuels, justificatifs de paiement, pièces société, pièces techniques et chantier
          </DialogDescription>
        </DialogHeader>

        {/* Zone de formulaire upload */}
        <div className="space-y-4 pt-2">
          {/* Type de document — groupé par catégorie */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Type de document *</Label>
            <Select value={typeDocument} onValueChange={setTypeDocument}>
              <SelectTrigger className="h-10" data-testid="doc-type-select">
                <SelectValue placeholder="Sélectionnez le type de document" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {(() => {
                  // Groupage par catégorie en préservant l'ordre d'apparition
                  const grouped = PICKER_TYPES.reduce((acc, dt) => {
                    const cat = dt.category || 'Personnalisé';
                    if (!acc.has(cat)) acc.set(cat, []);
                    acc.get(cat).push(dt);
                    return acc;
                  }, new Map());
                  return Array.from(grouped.entries()).map(([category, items]) => (
                    <div key={category} className="px-1 pt-2 first:pt-0">
                      <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500 border-b border-gray-100 mb-1">
                        {category}
                      </div>
                      {items.map(dt => (
                        <SelectItem key={dt.value} value={dt.value} data-testid={`doc-type-option-${dt.value}`}>
                          <span className="flex items-center gap-2">
                            <dt.icon className="h-3.5 w-3.5" />
                            {dt.label}
                          </span>
                        </SelectItem>
                      ))}
                    </div>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>

          {/* Description optionnelle */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description (optionnelle)</Label>
            <Input
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Ex: Attestation annuelle 2026 signee par le membre"
              className="h-9" data-testid="doc-description-input"
            />
          </div>

          {/* V36 — Date d'origine (antériorité) avec auto-détection */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium flex items-center gap-1.5">
                <Clock className="h-3 w-3 text-blue-500" />
                Date d&apos;origine du document (optionnelle)
              </Label>
              {dateOrigineAuto && dateOrigine && (
                <span className="text-[9px] font-bold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded-full" title="Date détectée automatiquement dans le nom du fichier">
                  ↻ AUTO-DÉTECTÉE
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <DatePickerFr
                value={dateOrigine}
                onChange={(v) => { setDateOrigine(v); setDateOrigineAuto(false); }}
                className="h-9 max-w-[180px]"
                data-testid="doc-date-origine-input"
              />
              {dateOrigine && (
                <button
                  type="button"
                  onClick={() => { setDateOrigine(''); setDateOrigineAuto(false); }}
                  className="text-[11px] text-gray-500 hover:text-red-600 underline"
                  data-testid="doc-date-origine-clear"
                >
                  effacer
                </button>
              )}
            </div>
            <p className="text-[10px] text-gray-500 leading-tight">
              Si le document est <strong>antérieur au 01/05/2026</strong>, indiquez sa date d&apos;origine
              pour la continuité historique (GED + relevés RCDS/FTPE).
              {' '}La date est détectée automatiquement depuis le nom du fichier (ex: <code>attestation-2024-05.pdf</code>).
            </p>
          </div>

          {/* Zone de drop */}
          <div
            className={cn(
              "border-2 border-dashed rounded-lg p-6 text-center transition-all cursor-pointer",
              dragOver ? "border-teal-500 bg-teal-50" : file ? "border-emerald-300 bg-emerald-50" : "border-gray-300 hover:border-teal-400 hover:bg-gray-50"
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            data-testid="doc-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              onChange={handleFileChange}
              className="hidden"
              data-testid="doc-file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-emerald-600" />
                <div className="text-left">
                  <p className="text-sm font-medium text-emerald-800">{file.name}</p>
                  <p className="text-xs text-emerald-600">{formatSize(file.size)}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50"
                  onClick={(e) => { e.stopPropagation(); setFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                <p className="text-sm font-medium text-gray-700">Glissez-deposez un fichier ici</p>
                <p className="text-xs text-gray-500 mt-1">ou cliquez pour selectionner (PDF, PNG, JPEG - max 20 Mo)</p>
              </>
            )}
          </div>

          <Button
            onClick={handleUpload}
            disabled={uploading || !file || !typeDocument}
            className="w-full bg-teal-600 hover:bg-teal-700 h-10"
            data-testid="upload-submit-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            Inserer le document
          </Button>

          {/* Panneau Super Admin — Gérer les types de documents personnalisés */}
          {userRole === 'super_admin' && (
            <CustomDocumentTypesAdminPanel token={token} />
          )}

          {/* Panneau Audit RGPD — Journal d'activité (super_admin / admin) */}
          {(userRole === 'super_admin' || userRole === 'admin') && membre?.id && (
            <AuditLogPanel membreId={membre.id} token={token} />
          )}
        </div>

        {/* Documents existants */}
        <div className="pt-4 border-t mt-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-500" />
              Documents inseres ({documents.length})
            </h4>
            {documents.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2.5 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={() => handleExportZip()}
                disabled={exportingZip}
                title="Télécharger tous les documents visibles (respecte les filtres actifs)"
                data-testid="export-zip-btn"
              >
                {exportingZip ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}
                ZIP
              </Button>
            )}
          </div>
          {/* Barre de recherche + Filtres rapides par catégorie — visibles si >0 documents */}
          {!loadingDocs && documents.length > 0 && (() => {
            // Helper de normalisation : lowercase + suppression des accents pour
            // que "Kbis" matche "KBIS 2024" et "réception" matche "Reception".
            const normalize = (s) => (s || '')
              .toString()
              .toLowerCase()
              .normalize('NFD')
              .replace(/[\u0300-\u036f]/g, '');
            const q = normalize(searchQuery.trim());

            // 1) Filtre par recherche en premier (sur filename + type label)
            const searched = q
              ? documents.filter((doc) => {
                  const conf = [...ALL_DOC_TYPES, ...customTypes].find(d => d.value === doc.type_document);
                  const haystack = normalize(
                    `${doc.original_filename || ''} ${(conf && conf.label) || ''} ${doc.type_document || ''}`,
                  );
                  return haystack.includes(q);
                })
              : documents;

            // 2) Comptage par catégorie SUR le résultat de la recherche → les
            //    catégories vides disparaissent automatiquement
            const counts = new Map();
            counts.set('all', searched.length);
            for (const doc of searched) {
              const conf = [...ALL_DOC_TYPES, ...customTypes].find(d => d.value === doc.type_document);
              const cat = (conf && conf.category) || 'Autre';
              counts.set(cat, (counts.get(cat) || 0) + 1);
            }
            const canonicalOrder = Array.from(new Set([
              ...DOC_TYPES.map(d => d.category),
              ...customTypes.map(d => d.category || 'Personnalisé'),
              'Autre',
            ]));
            const visibleCats = canonicalOrder.filter(cat => counts.has(cat));

            return (
              <div className="space-y-2 mb-3">
                {/* Barre de recherche */}
                <div className="relative" data-testid="doc-search-wrapper">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                  <Input
                    type="text"
                    placeholder="Rechercher un document (ex : kbis 2024)…"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 text-xs pl-8 pr-8"
                    data-testid="doc-search-input"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 p-0.5"
                      title="Effacer la recherche"
                      aria-label="Effacer la recherche"
                      data-testid="doc-search-clear"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                {/* Filtres catégorie */}
                <div className="flex flex-wrap gap-1.5" data-testid="doc-category-filters">
                  <button
                    type="button"
                    onClick={() => setCategoryFilter('all')}
                    className={cn(
                      "text-[11px] px-2.5 py-1 rounded-full border transition-colors font-medium",
                      categoryFilter === 'all'
                        ? "bg-teal-600 text-white border-teal-700 shadow-sm"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
                    )}
                    data-testid="doc-category-filter-all"
                  >
                    Toutes ({counts.get('all')})
                  </button>
                  {visibleCats.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategoryFilter(cat)}
                      className={cn(
                        "text-[11px] px-2.5 py-1 rounded-full border transition-colors font-medium",
                        categoryFilter === cat
                          ? "bg-teal-600 text-white border-teal-700 shadow-sm"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
                      )}
                      data-testid={`doc-category-filter-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {cat} ({counts.get(cat)})
                    </button>
                  ))}
                </div>
              </div>
            );
          })()}
          {loadingDocs ? (
            <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-teal-500" /></div>
          ) : documents.length === 0 ? (
            <div className="py-6 text-center text-sm text-gray-400">Aucun document insere pour ce membre</div>
          ) : (() => {
            // Filtrer selon la recherche puis la catégorie sélectionnée
            const normalize = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
            const q = normalize(searchQuery.trim());
            const searched = q
              ? documents.filter((doc) => {
                  const conf = [...ALL_DOC_TYPES, ...customTypes].find(d => d.value === doc.type_document);
                  const haystack = normalize(
                    `${doc.original_filename || ''} ${(conf && conf.label) || ''} ${doc.type_document || ''}`,
                  );
                  return haystack.includes(q);
                })
              : documents;
            const filtered = categoryFilter === 'all'
              ? searched
              : searched.filter(doc => {
                  const conf = [...ALL_DOC_TYPES, ...customTypes].find(d => d.value === doc.type_document);
                  return ((conf && conf.category) || 'Autre') === categoryFilter;
                });
            if (filtered.length === 0) {
              return (
                <div className="py-6 text-center text-sm text-gray-400" data-testid="doc-category-empty">
                  {q
                    ? `Aucun document ne correspond à « ${searchQuery} »`
                    : 'Aucun document dans cette catégorie'}
                </div>
              );
            }
            return (
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {filtered.map(doc => {
                const typeConf = getTypeConfig(doc.type_document);
                return (
                  <div key={doc.id} className="flex items-center gap-3 p-2.5 rounded-lg border bg-white hover:bg-gray-50 transition-colors" data-testid={`membre-doc-${doc.id}`}>
                    <div className={cn("w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0", typeConf.color.split(' ')[0])}>
                      <typeConf.icon className={cn("h-4 w-4", typeConf.color.split(' ')[1])} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{doc.original_filename}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", typeConf.color)}>{typeConf.label}</Badge>
                        <span className="text-[10px] text-gray-400">{formatSize(doc.file_size)}</span>
                        <span className="text-[10px] text-gray-400">{new Date(doc.created_at).toLocaleDateString('fr-FR')}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleView(doc)} title="Visualiser" data-testid={`view-doc-${doc.id}`}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50" onClick={() => handleDownloadDoc(doc)} title="Telecharger" data-testid={`download-doc-${doc.id}`}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50" onClick={() => handlePrintDoc(doc)} title="Imprimer" data-testid={`print-doc-${doc.id}`}>
                        <Printer className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(doc.id)} disabled={deletingId === doc.id} title="Supprimer" data-testid={`delete-doc-${doc.id}`}>
                        {deletingId === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
